import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { useTranslation } from "react-i18next";

const COUNTRY_CONFIG = {
  Uganda: { code: "+256", min: 9, max: 9 },
  Kenya: { code: "+254", min: 9, max: 9 },
  USA: { code: "+1", min: 10, max: 10 },
  UK: { code: "+44", min: 10, max: 10 },
  Canada: { code: "+1", min: 10, max: 10 },
  India: { code: "+91", min: 10, max: 10 }
};

const LANGUAGE_OPTIONS = [
  { label: "Select Language", value: "" },
  { label: "English", value: "English" },
  { label: "Spanish", value: "Spanish" },
  { label: "French", value: "French" },
  { label: "Swahili", value: "Swahili" }
];

const ElderProfileSetup = () => {
  const navigate = useNavigate();
  const { t: tAuth } = useTranslation("auth");
  const { t: tProfile } = useTranslation("profile");
  const patientID = localStorage.getItem("patientID");
  const token = localStorage.getItem("token");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [spokenLanguage, setSpokenLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!patientID || !token) {
      navigate("/elder-login");
    }
  }, [navigate, patientID, token]);

  const handleCountryChange = (event) => {
    const selectedCountry = event.target.value;
    setCountry(selectedCountry);
    setCountryCode(COUNTRY_CONFIG[selectedCountry]?.code || "");
    setWhatsapp("");
    setErrors((prev) => ({ ...prev, country: "", whatsapp: "" }));
  };

  const handleWhatsappChange = (event) => {
    setWhatsapp(event.target.value.replace(/\D/g, ""));
    setErrors((prev) => ({ ...prev, whatsapp: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!firstName.trim()) nextErrors.firstName = tAuth("elderProfileSetup.errors.firstName");
    if (!lastName.trim()) nextErrors.lastName = tAuth("elderProfileSetup.errors.lastName");
    if (!spokenLanguage) nextErrors.spokenLanguage = tAuth("elderProfileSetup.errors.spokenLanguage");
    if (!gender) nextErrors.gender = tAuth("elderProfileSetup.errors.gender");
    if (!country) nextErrors.country = tAuth("elderProfileSetup.errors.country");
    if (!city.trim()) nextErrors.city = tAuth("elderProfileSetup.errors.city");
    if (!whatsapp.trim()) nextErrors.whatsapp = tAuth("elderProfileSetup.errors.whatsapp");

    const configForCountry = COUNTRY_CONFIG[country];
    if (
      whatsapp &&
      configForCountry &&
      (whatsapp.length < configForCountry.min || whatsapp.length > configForCountry.max)
    ) {
      nextErrors.whatsapp = tProfile("create.phoneLength", {
        min: configForCountry.min,
        max: configForCountry.max
      });
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await axios.put(
        `${config.API_BASE_URL}${config.PATIENT.COMPLETE_PROFILE}/${patientID}`,
        {
          firstName,
          lastName,
          spokenLanguage,
          gender,
          country,
          city,
          stateProvince,
          whatsapp: `${countryCode}${whatsapp}`
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      localStorage.setItem("profileComplete", "true");
      navigate("/elder-map");
    } catch (err) {
      setError(err.response?.data?.error || tAuth("elderProfileSetup.failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <section className="page-panel">
        <div className="page-content auth-layout profile-form-layout">
          <div className="support-card compact-support-card">
            <div>
              <div className="intro-badge">{tAuth("elderProfileSetup.badge")}</div>
              <h1 className="page-title">{tAuth("elderProfileSetup.title")}</h1>
              <p className="page-subtitle">{tAuth("elderProfileSetup.subtitle")}</p>
            </div>

            <div className="support-list">
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{tAuth("elderProfileSetup.support1Title")}</div>
                  <p>{tAuth("elderProfileSetup.support1Text")}</p>
                </div>
              </div>
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{tAuth("elderProfileSetup.support2Title")}</div>
                  <p>{tAuth("elderProfileSetup.support2Text")}</p>
                </div>
              </div>
              <div className="support-item">
                <span className="support-dot" />
                <div>
                  <div className="mini-label">{tAuth("elderProfileSetup.support3Title")}</div>
                  <p>{tAuth("elderProfileSetup.support3Text")}</p>
                </div>
              </div>
            </div>
          </div>

          <form className="form-card compact-form-card" onSubmit={handleSave}>
            <div className="form-header">
              <h2>{tAuth("elderProfileSetup.formTitle")}</h2>
              <p>{tAuth("elderProfileSetup.formSubtitle")}</p>
            </div>

            {error && <div className="error-banner">{error}</div>}

            <div className="form-grid">
              <Input
                label={tProfile("fields.firstName")}
                value={firstName}
                setValue={setFirstName}
                error={errors.firstName}
              />
              <Input
                label={tProfile("fields.lastName")}
                value={lastName}
                setValue={setLastName}
                error={errors.lastName}
              />

              <Select
                label={tProfile("fields.language")}
                value={spokenLanguage}
                onChange={(event) => setSpokenLanguage(event.target.value)}
                options={LANGUAGE_OPTIONS}
                error={errors.spokenLanguage}
              />
              <Select
                label={tProfile("fields.gender")}
                value={gender}
                onChange={(event) => setGender(event.target.value)}
                options={[
                  { label: tProfile("fields.selectGender"), value: "" },
                  { label: tProfile("fields.male"), value: "M" },
                  { label: tProfile("fields.female"), value: "F" }
                ]}
                error={errors.gender}
              />

              <Select
                label={tProfile("fields.country")}
                value={country}
                onChange={handleCountryChange}
                options={[
                  { label: tProfile("fields.selectCountry"), value: "" },
                  ...Object.keys(COUNTRY_CONFIG).map((countryName) => ({
                    label: countryName,
                    value: countryName
                  }))
                ]}
                error={errors.country}
              />
              <Input
                label={tProfile("fields.city")}
                value={city}
                setValue={setCity}
                error={errors.city}
              />

              <Input
                label={tProfile("fields.state")}
                value={stateProvince}
                setValue={setStateProvince}
              />

              <div className="form-field full">
                <label htmlFor="elder-profile-whatsapp">{tProfile("fields.whatsapp")}</label>
                <div className="phone-row">
                  <div className="code-chip">{countryCode || tProfile("fields.phoneCode")}</div>
                  <input
                    id="elder-profile-whatsapp"
                    className="text-input"
                    value={whatsapp}
                    onChange={handleWhatsappChange}
                    placeholder={tProfile("fields.whatsappPlaceholder")}
                    style={errors.whatsapp ? { borderColor: "#b0432f" } : undefined}
                  />
                </div>
                {errors.whatsapp && <span className="field-error">{errors.whatsapp}</span>}
              </div>
            </div>

            <div className="button-row">
              <button
                className="ghost-button"
                type="button"
                onClick={() => navigate("/elder-login")}
                disabled={loading}
              >
                ← Back to sign in
              </button>
              <button className="warm-button" type="submit" disabled={loading}>
                {loading ? tAuth("elderProfileSetup.submitting") : `${tAuth("elderProfileSetup.submit")} →`}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

const Input = ({ label, value, setValue, error, type = "text" }) => (
  <div className="form-field">
    <label>{label}</label>
    <input
      className="text-input"
      type={type}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      style={error ? { borderColor: "#b0432f" } : undefined}
    />
    {error && <span className="field-error">{error}</span>}
  </div>
);

const Select = ({ label, value, onChange, options, error }) => (
  <div className="form-field">
    <label>{label}</label>
    <select
      className="select-input"
      value={value}
      onChange={onChange}
      style={error ? { borderColor: "#b0432f" } : undefined}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && <span className="field-error">{error}</span>}
  </div>
);

export default ElderProfileSetup;
